import { Package } from 'lucide-react'
import { ParcelsSpaceFields } from '@/components/features/parcels/ParcelsSpaceFields'
import { apiFieldErrors, defaultParcelsConfig, mergeParcelsConfig, toParcelsInput, validateParcelsConfig } from '@/components/features/parcels/parcelUtils'
import { ParcelsRetentionConfirm } from '@/components/features/spaces/retention'
import { retentionChanges } from '@/components/features/spaces/retentionUtils'
import type { ModuleDefinition } from '../types'
import { lazyPage } from '@/lib/lazyPage'

const ParcelsPage = lazyPage(() => import('@/pages/Parcels'), 'ParcelsPage')
const ParcelDetailPage = lazyPage(() => import('@/pages/ParcelDetail'), 'ParcelDetailPage')

export const parcelsModule: ModuleDefinition = {
  key: 'parcels',
  icon: Package,
  iconName: 'package',
  namespaces: ['parcels'],
  ready: true,
  ListPage: ParcelsPage,
  /* `/{section}/{space}/{parcelId}`: the team and the recipient open a parcel; anyone else gets a 404 from the API. */
  renderDetail: ({ spaceData }) => <ParcelDetailPage spaceId={spaceData.id} spaceData={spaceData} />,
  /* Short setup on the details step (F-81 section 6.2), stored in `Space.moduleConfig.parcels`. */
  setup: {
    wizardStep: 'details',
    defaultConfig: () => defaultParcelsConfig(),
    validate: (config, t) => validateParcelsConfig(config, t),
    errorsFromApi: (err) => {
      /* The API names them `moduleConfig.parcels.<field>`; the form keys them by the last segment. */
      const fields = Object.fromEntries(Object.entries(apiFieldErrors(err)).map(([param, message]) => [param.split('.').pop() as string, message]))
      return fields.reminderAfterDays || fields.reminderMaxCount || fields.retentionMonths ? fields : null
    },
    Fields: ParcelsSpaceFields,
    /* F-203a: a new retention period is confirmed with what it would anonymize today. */
    confirmSave: {
      needed: (config, space) => retentionChanges('parcels', config, space),
      Dialog: ParcelsRetentionConfirm
    },
    persist: {
      type: 'moduleConfig',
      /* Retention and any other key of the stored object go back untouched. */
      toModuleConfig: (config, stored) => ({
        ...(stored || {}),
        parcels: { ...(stored?.parcels || {}), ...toParcelsInput(config) }
      }),
      fromSpace: (space) => mergeParcelsConfig(space.moduleConfig?.parcels)
    }
  }
}
